import { AtSign, Check, Plus, Zap } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { useApplyWithoutAskingStore } from '@/stores/apply-without-asking-store'
import { useChatStore } from '@/stores/chat-store'
import { useLanguageStore, SUPPORTED_LANGUAGES } from '@/stores/language-store'
import { usePanelSelectionStore } from '@/stores/panel-selection-store'
import { resolvePanelTitle, resolvePanelComponentType } from '@/workspace/panel-title'
import { useAiCredits } from './use-ai-credits'

interface OptionRowProps {
  label: string
  hint?: string
  active: boolean
  disabled?: boolean
  disabledHint?: string
  onSelect: () => void
}

/** One choice inside a submenu: label (+hint), check when active. */
function OptionRow({ label, hint, active, disabled, disabledHint, onSelect }: OptionRowProps) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={onSelect}
      aria-label={label}
      className="flex items-start gap-2 text-xs"
    >
      <span className="flex-1">
        <span className={cn('block', disabled && 'text-muted-foreground/50')}>{label}</span>
        {(hint || (disabled && disabledHint)) && (
          <span className="block text-[10px] text-muted-foreground/60">
            {disabled ? disabledHint : hint}
          </span>
        )}
      </span>
      {active && <Check size={13} className="mt-0.5 shrink-0 text-primary" />}
    </DropdownMenuItem>
  )
}

/** A top-level row: category left, current value + chevron right (the
 *  chevron comes from DropdownMenuSubTrigger). */
function CategoryTrigger({ label, value }: { label: string; value: string }) {
  return (
    <DropdownMenuSubTrigger className="flex items-center gap-2 text-xs" aria-label={label}>
      <span className="flex-1">{label}</span>
      <span className="text-muted-foreground">{value}</span>
    </DropdownMenuSubTrigger>
  )
}

/**
 * The `+` menu in the composer's controls row: panel mentions, the response
 * language and the apply-without-asking preference, with the metered-credits
 * footer. Absorbs the old CompanionOptionsMenu / ChatControls settings. There
 * is no mode to pick: every turn starts read-only, and the server decides per
 * message whether it may change the workspace.
 */
export function CompanionPlusMenu() {
  const language = useLanguageStore((s) => s.language)
  const setLanguage = useLanguageStore((s) => s.setLanguage)
  const languageLabel = SUPPORTED_LANGUAGES.find((l) => l.code === language)?.label ?? 'English'
  const creditsRemaining = useAiCredits()
  const applyWithoutAsking = useApplyWithoutAskingStore((s) => s.enabled)
  const setApplyWithoutAsking = useApplyWithoutAskingStore((s) => s.setEnabled)

  const startMention = () => {
    usePanelSelectionStore.getState().startSelection(
      null,
      'Click a panel to mention it →',
      (panelId) => {
        useChatStore.getState().addMention({
          panelId,
          componentType: resolvePanelComponentType(panelId) ?? 'unknown',
          title: resolvePanelTitle(panelId),
        })
      },
      () => {},
      // Reference-only pick — no "making changes" activity shimmer on the panel.
      { activityOnSelect: false },
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-hover hover:text-foreground"
          title="Add and options"
          aria-label="Companion options"
        >
          <Plus size={15} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="z-[80] w-52">
        <DropdownMenuItem
          onSelect={startMention}
          aria-label="Mention a panel"
          className="flex items-center gap-2 text-xs"
        >
          <AtSign size={13} className="text-muted-foreground" />
          Mention a panel
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuSub>
          <CategoryTrigger label="Language" value={languageLabel} />
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="z-[80] w-40" sideOffset={6}>
              {SUPPORTED_LANGUAGES.map((l) => (
                <OptionRow
                  key={l.code}
                  label={l.label}
                  active={language === l.code}
                  onSelect={() => setLanguage(l.code)}
                />
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />
        {/* A trust preference (2026-10-15): skips the ask-first card, except
            for changes that remove something. */}
        <DropdownMenuCheckboxItem
          checked={applyWithoutAsking}
          onCheckedChange={(checked) => setApplyWithoutAsking(checked === true)}
          onSelect={(e) => e.preventDefault()}
          className="text-xs"
        >
          Apply changes without asking
        </DropdownMenuCheckboxItem>

        {creditsRemaining !== null && (
          <>
            <DropdownMenuSeparator />
            <p className="flex items-center gap-1.5 px-2 py-1 text-[10px] text-muted-foreground/60">
              <Zap size={10} />
              {creditsRemaining} AI credits remaining
            </p>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
